import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery, isAuthError } from "../../providers/auth0-instance";
import { notifyQueryError } from "../notifications";

export interface RealtimeTicketResponse {
  /** Single use, and refused at connect once `expires_in` seconds have gone. */
  ticket: string;
  expires_in: number;
}

export const realtimeApi = createApi({
  reducerPath: "realtimeApi",
  baseQuery: axiosBaseQuery(),
  endpoints: (builder) => ({
    /**
     * A one-time ticket for opening the realtime socket.
     *
     * Goes through axiosBaseQuery like every other request, so the socket is
     * scoped by exactly what REST is: the selected organization's header, the
     * memberships on the token, and the sign-in redirect when the session has
     * gone. The JWT itself never appears in the socket URL.
     *
     * Only a session that has gone is reported, through the same once-per-load
     * message as the rest of the opening burst. Anything else is RealtimeProvider
     * retrying in the background while polling carries on, so a message for each
     * attempt would be a toast every minute about something the user cannot act
     * on and is not missing.
     */
    createRealtimeTicket: builder.mutation<RealtimeTicketResponse, void>({
      query: () => ({ url: "/realtime/ticket", method: "POST" }),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
        } catch (err) {
          if (isAuthError((err as { error?: unknown })?.error)) {
            notifyQueryError(dispatch, "Failed to open live updates", err);
          }
        }
      },
    }),
  }),
});

export const { useCreateRealtimeTicketMutation } = realtimeApi;
